<template>
  <div v-if="officeId">
    <div class="table-responsive" v-if="users">
      <p>選択中の利用者：{{selectedUserDisplayName}}</p>
      <div class="input-group">
        <div class="input-group-prepend" id="search-addon">
          <i class="input-group-text fa fa-search"></i>
        </div>
        <input type="text" class="form-control" placeholder="利用者名で絞り込み" v-model="conditions.keywords"
               @input.prevent="filter(null)"/>
      </div>
      <div class="user-list-outer">
        <table id="user-list" class="table table-sm table-bordered">
          <thead>
          <tr>
            <th @click="filter(conditions.sortDirection * -1)" class="sortable sorted"
                :class="{asc: conditions.sortDirection > 0, desc: conditions.sortDirection < 0}">
              利用者名
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="user in users" :key="user.id" :class="{'table-info': selectedValue === user.id}">
            <td @click="selectUser(user.id)">
              {{user.display_name}}
              <template v-if="user.display_name_kana">
                ({{user.display_name_kana}})
              </template>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="text-center" v-show="isProcessing">
      <div class="spinner-border" style="width: 3rem; height: 3rem;" role="status"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import _ from 'underscore'
import api from '../libs/Api'
import Constants from '../constants'

const props = defineProps({
  modelValue: {
    type: [String, Number, null],
    default: null
  },
  officeId: {
    type: [String, Number, null],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const store = useStore()
const users = ref(null)
const orgUsers = ref([])
const conditions = ref({
  keywords: null,
  sortDirection: 1
})
const selectedValue = ref('')
const isProcessing = ref(false)

const selectedUserDisplayName = computed(() => {
  if (!users.value) return
  const filteredUsers = _.filter(users.value, user => user.id === selectedValue.value)
  return filteredUsers.length > 0 ? filteredUsers[0].display_name : '(未選択)'
})

const load = async () => {
  users.value = null
  if (!props.officeId) return

  isProcessing.value = true
  try {
    const response = await api.getUser(Constants.QUERY_MODE_TRIM_GET, 1, props.officeId)
    users.value = response
    orgUsers.value = response
    if (!users.value) return
    filter()
  } catch (error) {
    console.error('Error loading users:', error)
  } finally {
    isProcessing.value = false
  }
}

onMounted(async () => {
  const savedConditions = store.state.userlist.savedConditions
  if (savedConditions) {
    conditions.value = savedConditions
  }

  if (props.officeId) {
    await load()
  }
})

watch(() => props.officeId, async (newVal, oldVal) => {
  if (newVal === oldVal) return
  await load()
}, { immediate: true })

const filter = (direction = null) => {
  if (!orgUsers.value) return

  if (direction) {
    conditions.value.sortDirection = direction
  }

  if (conditions.value.keywords) {
    users.value = _.filter(orgUsers.value, user => {
      if (user.display_name_kana && user.display_name_kana.indexOf(conditions.value.keywords) >= 0) return true
      if (user.display_name.indexOf(conditions.value.keywords) >= 0) return true
      return false
    })
  } else {
    users.value = orgUsers.value
  }

  users.value.sort((a, b) => {
    if (a.display_name_kana > b.display_name_kana) return 1 * conditions.value.sortDirection
    if (a.display_name_kana < b.display_name_kana) return -1 * conditions.value.sortDirection
    if (a.display_name > b.display_name) return 1 * conditions.value.sortDirection
    if (a.display_name < b.display_name) return -1 * conditions.value.sortDirection
    return 0
  })

  store.dispatch('userlist/setConditions', conditions.value)

  const targetUserId = store.state.careplan.targetUserId
  const targetUsers = _.filter(users.value, user => user.id === targetUserId)

  if (targetUsers.length > 0) {
    selectUser(targetUserId)
  } else {
    selectUser()
  }
}

const selectUser = (id = '') => {
  if (selectedValue.value === id) return
  selectedValue.value = id
  emit('update:modelValue', id)
  emit('change', Event.srcElement)
}
</script>

<style lang="scss" scoped>
#user-list tr {
  cursor: pointer;
}

.user-list-outer {
  height: 550px;
  overflow-y: scroll;
}

th {
  &.sorted {
    &.asc::after {
      display: inline-block;
      content: '▲';
    }

    &.desc::after {
      display: inline-block;
      content: '▼';
    }
  }
}

.sortable {
  cursor: pointer;
  text-decoration: underline;
}
</style>
